import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { NameGeneratorForm } from '../components/name-generator/NameGeneratorForm';
import { NameSuggestion } from '../types/name';
import { SeoMeta } from '../components/seo/SeoMeta';
import { generateChineseName } from '../lib/api/nameGenerator';
import { Download, Palette, Type, Layout } from 'lucide-react';
import { ProgressBar } from '../components/ui/ProgressBar';

// Card themes configuration
const cardThemes = {
  classic: {
    name: "Classic Red",
    gradient: "from-red-600 to-red-800",
    bgPattern: "bg-gradient-to-br from-red-50 to-red-100",
    textColor: "text-gray-800",
    accentColor: "text-red-600"
  },
  ocean: {
    name: "Ocean Blue",
    gradient: "from-blue-600 to-indigo-800",
    bgPattern: "bg-gradient-to-br from-blue-50 to-indigo-100",
    textColor: "text-gray-800",
    accentColor: "text-blue-600"
  },
  nature: {
    name: "Nature Green",
    gradient: "from-green-600 to-teal-800",
    bgPattern: "bg-gradient-to-br from-green-50 to-teal-100",
    textColor: "text-gray-800",
    accentColor: "text-green-600"
  },
  gold: {
    name: "Golden Elegance",
    gradient: "from-yellow-600 to-amber-800",
    bgPattern: "bg-gradient-to-br from-yellow-50 to-amber-100",
    textColor: "text-gray-800",
    accentColor: "text-amber-600"
  }
};

// Card layouts configuration
const cardLayouts = {
  vertical: { width: 400, height: 600, orientation: "vertical" },
  horizontal: { width: 600, height: 400, orientation: "horizontal" },
  square: { width: 500, height: 500, orientation: "square" }
};

export const NameGenerator: React.FC = () => {
  const location = useLocation();
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');
  
  // 卡片设计状态
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [selectedLayout, setSelectedLayout] = useState('vertical');
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('large');

  // 检查是否有来自主页的参数
  useEffect(() => {
    const state = location.state as { firstName?: string; surname?: string; autoGenerate?: boolean } | null;
    
    if (state?.autoGenerate && state.firstName && state.surname) {
      // 自动开始生成中文名
      handleGenerateNames(state.firstName, state.surname);
    }
  }, [location.state]);

  const handleGenerateNames = async (firstName: string, surname: string) => {
    setIsLoading(true);
    setError(null);
    setProgress('Analyzing your name meaning...');
    setSuggestions([]);
    
    try {
      // 更新进度提示
      setTimeout(() => setProgress('Creating Chinese name suggestions...'), 1000);
      setTimeout(() => setProgress('Adding cultural meanings...'), 2000);
      
      const response = await generateChineseName(firstName, surname);
      
      // Update suggestions list
      setSuggestions(response.suggestions);
      setSelectedIndex(0);
      
      // 滚动到结果区域
      setTimeout(() => {
        const resultsElement = document.getElementById('name-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error occurred while generating names');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
      setProgress('');
    }
  };

  // 获取初始表单值
  const getInitialFormValues = () => {
    const state = location.state as { firstName?: string; surname?: string } | null;
    return {
      firstName: state?.firstName || '',
      surname: state?.surname || ''
    };
  };

  // 获取当前主题和布局
  const currentTheme = cardThemes[selectedTheme as keyof typeof cardThemes];
  const currentLayout = cardLayouts[selectedLayout as keyof typeof cardLayouts];
  const currentSuggestion = suggestions[selectedIndex];

  // 生成高质量卡片图片的函数 - 与预览完全一致
  const generateCardImage = (suggestionIndex?: number) => {
    const nameData = suggestions[suggestionIndex ?? selectedIndex];
    if (!nameData) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = 2; // 高分辨率缩放
    
    canvas.width = currentLayout.width * scale;
    canvas.height = currentLayout.height * scale;
    ctx.scale(scale, scale);

    // 背景渐变
    const gradient = ctx.createLinearGradient(0, 0, currentLayout.width, currentLayout.height);
    if (selectedTheme === 'classic') {
      gradient.addColorStop(0, '#FEFEFE');
      gradient.addColorStop(1, '#FEE2E2');
    } else if (selectedTheme === 'ocean') {
      gradient.addColorStop(0, '#FEFEFE');
      gradient.addColorStop(1, '#DBEAFE');
    } else if (selectedTheme === 'nature') {
      gradient.addColorStop(0, '#FEFEFE');
      gradient.addColorStop(1, '#D1FAE5');
    } else {
      gradient.addColorStop(0, '#FEFEFE');
      gradient.addColorStop(1, '#FEF3C7');
    }
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, currentLayout.width, currentLayout.height);

    // 主边框
    ctx.strokeStyle = selectedTheme === 'classic' ? '#FECACA' : 
                     selectedTheme === 'ocean' ? '#BFDBFE' :
                     selectedTheme === 'nature' ? '#A7F3D0' : '#FDE68A';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, currentLayout.width - 8, currentLayout.height - 8);

    // 装饰性内边框
    ctx.strokeStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
    ctx.lineWidth = 2;
    ctx.strokeRect(20, 20, currentLayout.width - 40, currentLayout.height - 40);

    // 文字换行函数
    const wrapText = (text: string, x: number, y: number, maxWidth: number, lineHeight: number, textAlign: CanvasTextAlign = 'left') => {
      ctx.textAlign = textAlign;
      const words = text.split(' ');
      let line = '';
      let currentY = y;
      
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        if (testWidth > maxWidth && n > 0) {
          ctx.fillText(line, x, currentY);
          line = words[n] + ' ';
          currentY += lineHeight;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, x, currentY);
      return currentY + lineHeight;
    };

    if (currentLayout.orientation === 'vertical') {
      // 垂直布局 - 完整显示所有字段
      const centerX = currentLayout.width / 2;
      
      // 中文名字
      const chineseFontSize = fontSize === 'small' ? 48 : fontSize === 'medium' ? 64 : 80;
      ctx.font = `bold ${chineseFontSize}px "Microsoft YaHei", "PingFang SC", sans-serif`;
      ctx.fillStyle = '#1F2937';
      ctx.textAlign = 'center';
      ctx.fillText(nameData.chineseName, centerX, 100);
      
      // 拼音
      const pinyinSize = fontSize === 'small' ? 16 : fontSize === 'medium' ? 20 : 24;
      ctx.font = `${pinyinSize}px Arial, sans-serif`;
      ctx.fillStyle = '#6B7280';
      ctx.fillText(nameData.pinyin, centerX, 130);
      
      // 英文名
      if (nameData.englishName) {
        const englishSize = fontSize === 'small' ? 14 : fontSize === 'medium' ? 16 : 18;
        ctx.font = `italic ${englishSize}px Georgia, serif`;
        ctx.fillStyle = '#9CA3AF';
        ctx.fillText(nameData.englishName, centerX, 155);
      }
      
      // 分隔线
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(40, 180);
      ctx.lineTo(currentLayout.width - 40, 180);
      ctx.stroke();
      
      // 详细信息区域
      let yPos = 210;
      const sectionSpacing = 35;
      const maxWidth = currentLayout.width - 80;
      
      // Surname Explanation
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 16px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('👨‍👩‍👧‍👦 Surname Explanation', 40, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '14px Arial, sans-serif';
      yPos = wrapText(nameData.surnameExplanation, 40, yPos + 20, maxWidth, 18);
      
      yPos += sectionSpacing;
      
      // Given Name Meaning
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 16px Arial, sans-serif';
      ctx.fillText('✨ Given Name Meaning', 40, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '14px Arial, sans-serif';
      yPos = wrapText(nameData.givenNameExplanation, 40, yPos + 20, maxWidth, 18);
      
      yPos += sectionSpacing;
      
      // Cultural Connection
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 16px Arial, sans-serif';
      ctx.fillText('🏮 Cultural Connection', 40, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '14px Arial, sans-serif';
      yPos = wrapText(nameData.culturalConnection, 40, yPos + 20, maxWidth, 18);
      
      // Additional Notes (如果有空间)
      if (nameData.additionalNotes && yPos < currentLayout.height - 80) {
        yPos += sectionSpacing;
        ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                       selectedTheme === 'ocean' ? '#2563EB' :
                       selectedTheme === 'nature' ? '#059669' : '#D97706';
        ctx.font = 'bold 16px Arial, sans-serif';
        ctx.fillText('💡 Additional Notes', 40, yPos);
        
        ctx.fillStyle = '#4B5563';
        ctx.font = '14px Arial, sans-serif';
        wrapText(nameData.additionalNotes, 40, yPos + 20, maxWidth, 18);
      }
      
    } else if (currentLayout.orientation === 'horizontal') {
      // 水平布局
      const centerX = currentLayout.width / 4;
      const rightSideX = currentLayout.width / 2 + 20;
      const rightSideWidth = currentLayout.width / 2 - 60;
      
      // 左侧：名字信息
      const chineseFontSize = fontSize === 'small' ? 36 : fontSize === 'medium' ? 48 : 60;
      ctx.font = `bold ${chineseFontSize}px "Microsoft YaHei", "PingFang SC", sans-serif`;
      ctx.fillStyle = '#1F2937';
      ctx.textAlign = 'center';
      ctx.fillText(nameData.chineseName, centerX, 120);
      
      const pinyinSize = fontSize === 'small' ? 14 : fontSize === 'medium' ? 18 : 20;
      ctx.font = `${pinyinSize}px Arial, sans-serif`;
      ctx.fillStyle = '#6B7280';
      ctx.fillText(nameData.pinyin, centerX, 150);
      
      if (nameData.englishName) {
        const englishSize = fontSize === 'small' ? 12 : fontSize === 'medium' ? 14 : 16;
        ctx.font = `italic ${englishSize}px Georgia, serif`;
        ctx.fillStyle = '#9CA3AF';
        ctx.fillText(nameData.englishName, centerX, 175);
      }
      
      // 垂直分隔线
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(currentLayout.width / 2, 40);
      ctx.lineTo(currentLayout.width / 2, currentLayout.height - 40);
      ctx.stroke();
      
      // 右侧：详细信息
      let yPos = 80;
      const sectionSpacing = 25;
      
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('👨‍👩‍👧‍👦 Surname', rightSideX, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '12px Arial, sans-serif';
      yPos = wrapText(nameData.surnameExplanation.substring(0, 120) + '...', rightSideX, yPos + 15, rightSideWidth, 14);
      
      yPos += sectionSpacing;
      
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.fillText('✨ Given Name', rightSideX, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '12px Arial, sans-serif';
      yPos = wrapText(nameData.givenNameExplanation.substring(0, 120) + '...', rightSideX, yPos + 15, rightSideWidth, 14);
      
      yPos += sectionSpacing;
      
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.fillText('🏮 Cultural', rightSideX, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '12px Arial, sans-serif';
      wrapText(nameData.culturalConnection.substring(0, 120) + '...', rightSideX, yPos + 15, rightSideWidth, 14);
      
    } else {
      // 正方形布局
      const centerX = currentLayout.width / 2;
      
      // 标题
      const chineseFontSize = fontSize === 'small' ? 42 : fontSize === 'medium' ? 56 : 70;
      ctx.font = `bold ${chineseFontSize}px "Microsoft YaHei", "PingFang SC", sans-serif`;
      ctx.fillStyle = '#1F2937';
      ctx.textAlign = 'center';
      ctx.fillText(nameData.chineseName, centerX, 90);
      
      const pinyinSize = fontSize === 'small' ? 16 : fontSize === 'medium' ? 20 : 24;
      ctx.font = `${pinyinSize}px Arial, sans-serif`;
      ctx.fillStyle = '#6B7280';
      ctx.fillText(nameData.pinyin, centerX, 120);
      
      if (nameData.englishName) {
        const englishSize = fontSize === 'small' ? 14 : fontSize === 'medium' ? 16 : 18;
        ctx.font = `italic ${englishSize}px Georgia, serif`;
        ctx.fillStyle = '#9CA3AF';
        ctx.fillText(nameData.englishName, centerX, 145);
      }
      
      // 分隔线
      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(40, 170);
      ctx.lineTo(currentLayout.width - 40, 170);
      ctx.stroke();
      
      // 紧凑的详细信息
      let yPos = 200;
      const sectionSpacing = 28;
      const maxWidth = currentLayout.width - 80;
      
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('👨‍👩‍👧‍👦 Surname', 40, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '12px Arial, sans-serif';
      const surnameText = nameData.surnameExplanation.length > 80 ? 
        nameData.surnameExplanation.substring(0, 80) + '...' : nameData.surnameExplanation;
      yPos = wrapText(surnameText, 40, yPos + 15, maxWidth, 14);
      
      yPos += sectionSpacing;
      
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.fillText('✨ Given Name', 40, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '12px Arial, sans-serif';
      const givenNameText = nameData.givenNameExplanation.length > 80 ? 
        nameData.givenNameExplanation.substring(0, 80) + '...' : nameData.givenNameExplanation;
      yPos = wrapText(givenNameText, 40, yPos + 15, maxWidth, 14);
      
      yPos += sectionSpacing;
      
      ctx.fillStyle = selectedTheme === 'classic' ? '#DC2626' : 
                     selectedTheme === 'ocean' ? '#2563EB' :
                     selectedTheme === 'nature' ? '#059669' : '#D97706';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.fillText('🏮 Cultural', 40, yPos);
      
      ctx.fillStyle = '#4B5563';
      ctx.font = '12px Arial, sans-serif';
      const culturalText = nameData.culturalConnection.length > 80 ? 
        nameData.culturalConnection.substring(0, 80) + '...' : nameData.culturalConnection;
      wrapText(culturalText, 40, yPos + 15, maxWidth, 14);
    }

    // 装饰性角落
    const cornerSize = 20;
    const cornerColor = selectedTheme === 'classic' ? '#FECACA' : 
                       selectedTheme === 'ocean' ? '#BFDBFE' :
                       selectedTheme === 'nature' ? '#A7F3D0' : '#FDE68A';
    
    ctx.strokeStyle = cornerColor;
    ctx.lineWidth = 3;
    
    // 左上角
    ctx.beginPath();
    ctx.moveTo(30, 30 + cornerSize);
    ctx.lineTo(30, 30);
    ctx.lineTo(30 + cornerSize, 30);
    ctx.stroke();
    
    // 右下角
    ctx.beginPath();
    ctx.moveTo(currentLayout.width - 30, currentLayout.height - 30 - cornerSize);
    ctx.lineTo(currentLayout.width - 30, currentLayout.height - 30);
    ctx.lineTo(currentLayout.width - 30 - cornerSize, currentLayout.height - 30);
    ctx.stroke();

    // 下载图像
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chinese-name-card-${nameData.chineseName.replace(/\s+/g, '-')}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    }, 'image/png', 1.0);
  };

  // 下载所有卡片
  const downloadAllCards = () => {
    suggestions.forEach((_, index) => {
      setTimeout(() => generateCardImage(index), index * 1000);
    });
  };

  return (
    <div className="container mx-auto px-4 py-4 max-w-7xl">
      <SeoMeta
        title="Chinese Name Generator - Create Yours | Chinese-Name.net"
        description="Chinese name generator with meanings, pinyin, and cultural fit. Create an authentic Chinese name online and compare personalized suggestions instantly."
        pathname="/chinese-name-generator"
      />
      <div className="bg-gray-50 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900">Chinese Name Generator</span>
          </nav>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-extrabold text-gray-900 mb-6">
              Chinese Name Generator
            </h1>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
              Transform your English name into a meaningful Chinese name with authentic pronunciation, beautiful character meanings, and cultural significance.
            </p>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Free, instant, and culturally accurate. No registration required.
            </p>
          </div>

          {/* CTA Form */}
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-xl p-8">
            <NameGeneratorForm 
          onSubmit={handleGenerateNames} 
          isLoading={isLoading}
          initialValues={getInitialFormValues()}
        />
        
            {/* Progress Bar Component */}
            <ProgressBar isGenerating={isLoading} duration={25000} />
            
            {/* 错误提示 */}
            {error && (
              <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Introduce Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What is a Chinese Name Generator?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A Chinese name generator is an AI-powered tool that creates authentic Chinese names based on your English name, preferences, and cultural context. It combines phonetic translation, meaning analysis, and cultural significance to generate names that are both beautiful and meaningful.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Use Our Chinese Name Generator
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              The best and most accurate Chinese name generator for creating authentic, meaningful names with cultural significance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🆓</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Free & Instant</h3>
              <p className="text-gray-600">Generate unlimited Chinese names instantly at no cost. No registration required.</p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🤖</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">AI-Powered</h3>
              <p className="text-gray-600">Advanced AI technology ensures authentic and meaningful names with cultural accuracy.</p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🎨</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Beautiful Cards</h3>
              <p className="text-gray-600">Download elegant name cards to share your Chinese name on social media or business cards.</p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🏮</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Cultural Accuracy</h3>
              <p className="text-gray-600">Respects traditional Chinese naming conventions and meanings for authentic results.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Following Document Structure */}
      {/* Note: Generate a Chinese Name Instantly is already in Hero Section */}

      {/* H2: Chinese Name Generator with Meaning */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese Name Generator with Meaning
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Every Chinese name we generate includes detailed meanings, character explanations, and cultural significance. Understand how Chinese name meanings are chosen based on virtues, nature elements, and aspirations.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
              Chinese ai name
            </h3>
            <p className="text-gray-600 mb-6 text-center">
              Our AI Chinese name generator analyzes tone, five-element balance, and your personal story to become the leading Chinese ai name studio online.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-3 text-center">⚖️</div>
                <h4 className="font-semibold text-gray-900 mb-2 text-center">Balanced Meanings</h4>
                <p className="text-gray-600 text-sm text-center">
                  We use five-element harmony plus classic virtues to deliver the perfect Chinese name meaning for your family.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-3 text-center">🧠</div>
                <h4 className="font-semibold text-gray-900 mb-2 text-center">Semantic Match</h4>
                <p className="text-gray-600 text-sm text-center">
                  Names in Chinese and meanings are paired so your new name reflects both sound and spirit.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="text-3xl mb-3 text-center">✨</div>
                <h4 className="font-semibold text-gray-900 mb-2 text-center">Cultural Connection</h4>
                <p className="text-gray-600 text-sm text-center">
                  Characters are chosen for beauty, symbolism, and positive intent, making your Chinese names meaningful and memorable.
                </p>
              </div>
            </div>
            <div className="mt-10 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese name meaning</h3>
                <p className="text-gray-600">
                  Each suggestion explains the Chinese name meaning in plain English, helping you understand why the characters were paired and how they relate to you.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Names in Chinese and meanings</h3>
                <p className="text-gray-600">
                  We make it easy to compare the characters, the pronunciation, and the meanings so you can share your Chinese names with confidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* H2: Chinese Name Generator for Foreigners */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese Name Generator for Foreigners
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Perfect for foreigners, students, and professionals wanting authentic Chinese names. Our generator creates names that sound natural and culturally appropriate.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
              English Name to Chinese Name Mapping
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">🔊 Phonetic Matching</h4>
                <p className="text-gray-600 text-sm">We match sounds phonetically to create names that sound natural when pronounced.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">🌏 Cultural Adaptation</h4>
                <p className="text-gray-600 text-sm">Names are adapted culturally to ensure they work well in Chinese-speaking contexts.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">✅ Avoid Ambiguity</h4>
                <p className="text-gray-600 text-sm">We avoid ambiguous meanings to create names that work perfectly for English speakers.</p>
              </div>
            </div>
            <div className="mt-10 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">What is my Chinese name</h3>
                <p className="text-gray-600">
                  We answer "what is my Chinese name" instantly by generating three unique translations and explaining which characters suit your values.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Chinese name translation</h3>
                <p className="text-gray-600">
                  Use this Chinese names into English and English-to-Chinese translation process to reverse engineer how meanings map across languages, including Chinese American names and international variants.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* H2: Chinese name generator male */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese name generator male
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Create powerful, modern, or traditional Chinese male names that honor strength, integrity, and leadership. Each suggestion blends masculine energy with poetic meaning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Chinese names for boys</h3>
              <p className="text-gray-600 mb-4">
                Choose Chinese names for boys that pair your surname with masculine virtues. Our male Chinese name generator suggests names that fit every vibe.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Chinese names male, male Chinese given names, Chinese male first names, Chinese man’s name
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Male Chinese name generator</h3>
              <p className="text-gray-600 mb-4">
                Balance strength with meaning to get Chinese boys name meanings that inspire confidence. Mix dragons, virtues, and stars for modern flare.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: male Chinese names, male Chinese name generator, Chinese boys name meanings
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* H2: Chinese name generator female */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese name generator female
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Craft elegant, gentle, or bold Chinese female names that radiate beauty, grace, and cultural depth. Every name is designed for modern women and girls who love meaningful stories.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Chinese names list female</h3>
              <p className="text-gray-600 mb-4">
                Explore a curated Chinese names list female that includes cute, elegant, and traditional suggestions tied to graceful meanings.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: female Chinese names, Chinese names girl, girl Chinese names, Chinese girls names
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Beautiful Chinese girl names with meaning</h3>
              <p className="text-gray-600 mb-4">
                Get female Chinese names with meaning that celebrate beauty, kindness, and spirit. Our generator highlights Chinese woman names and Chinese women names that feel personal and refined.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Chinese girl names with meaning, female Chinese name generator
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Summary CTA Section */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Chinese Name Generator</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Whether you need a male, female, or translation-focused Chinese name, our generator handles every intent with care and cultural accuracy.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Start Generating →
          </button>
        </div>
      </section>
      {/* 整合的中文名字卡片设计器 */}
      {suggestions.length > 0 && (
        <div id="name-results">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              🎨 Your Chinese Name Card Designer
            </h2>
            <p className="text-lg text-gray-600">
              Complete name card customization with all design options in one place
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-xl max-w-7xl mx-auto">
            <div className="p-4">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                
                {/* 左侧控制面板 */}
                <div className="space-y-4">
                  {/* 名字选择 */}
                  <div className="bg-gray-50 rounded-lg shadow-sm p-4">
                    <h3 className="text-lg font-semibold mb-4">Select Name</h3>
                    <div className="space-y-2">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedIndex(index)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                            selectedIndex === index
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-semibold text-lg">{suggestion.chineseName}</div>
                          <div className="text-sm text-gray-600">{suggestion.pinyin}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 主题选择 */}
                  <div className="bg-gray-50 rounded-lg shadow-sm p-4">
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Palette className="mr-2 h-5 w-5" />
                      Card Theme
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(cardThemes).map(([key, theme]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedTheme(key)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            selectedTheme === key
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className={`h-6 w-full rounded bg-gradient-to-r ${theme.gradient} mb-2`}></div>
                          <span className="text-sm font-medium">{theme.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 布局选择 */}
                  <div className="bg-gray-50 rounded-lg shadow-sm p-4">
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Layout className="mr-2 h-5 w-5" />
                      Card Layout
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(cardLayouts).map(([key, layout]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedLayout(key)}
                          className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                            selectedLayout === key
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="font-medium">
                            {key === 'vertical' ? 'Vertical' : key === 'horizontal' ? 'Horizontal' : 'Square'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {layout.width} × {layout.height}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 字体大小 */}
                  <div className="bg-gray-50 rounded-lg shadow-sm p-4">
                    <h3 className="text-lg font-semibold mb-3 flex items-center">
                      <Type className="mr-2 h-5 w-5" />
                      Font Size
                    </h3>
                    <div className="flex gap-3">
                      {(['small', 'medium', 'large'] as const).map((size) => (
                        <button
                          key={size}
                          onClick={() => setFontSize(size)}
                          className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                            fontSize === size
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {size === 'small' ? 'Small' : size === 'medium' ? 'Medium' : 'Large'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 下载按钮 */}
                  <div className="space-y-3">
                    <button
                      onClick={() => generateCardImage()}
                      className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download Selected Card
                    </button>
                    <button
                      onClick={downloadAllCards}
                      className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors flex items-center justify-center"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Download All Cards ({suggestions.length})
                    </button>
                  </div>
                </div>

                {/* 中间：名字详细信息 */}
                <div className="space-y-6">
                  {currentSuggestion && (
                    <div className="bg-gray-50 rounded-lg shadow-sm p-6">
                      <h3 className="text-lg font-semibold mb-4">Name Details</h3>
                      <div className="space-y-4">
                        {/* Surname Explanation */}
                        <div>
                          <h4 className="text-sm font-semibold text-indigo-600 mb-2 flex items-center">
                            <span className="mr-2">👨‍👩‍👧‍👦</span>
                            Surname Explanation
                          </h4>
                          <p className="text-sm text-gray-700 leading-relaxed bg-white p-3 rounded">
                            {currentSuggestion.surnameExplanation}
                          </p>
                        </div>

                        {/* Given Name Meaning */}
                        <div>
                          <h4 className="text-sm font-semibold text-indigo-600 mb-2 flex items-center">
                            <span className="mr-2">✨</span>
                            Given Name Meaning
                          </h4>
                          <p className="text-sm text-gray-700 leading-relaxed bg-white p-3 rounded">
                            {currentSuggestion.givenNameExplanation}
                          </p>
                        </div>

                        {/* Cultural Connection */}
                        <div>
                          <h4 className="text-sm font-semibold text-indigo-600 mb-2 flex items-center">
                            <span className="mr-2">🏮</span>
                            Cultural Connection
                          </h4>
                          <p className="text-sm text-gray-700 leading-relaxed bg-white p-3 rounded">
                            {currentSuggestion.culturalConnection}
                          </p>
                        </div>

                        {/* Additional Notes */}
                        {currentSuggestion.additionalNotes && (
                          <div>
                            <h4 className="text-sm font-semibold text-indigo-600 mb-2 flex items-center">
                              <span className="mr-2">💡</span>
                              Additional Notes
                            </h4>
                            <p className="text-sm text-gray-700 leading-relaxed bg-white p-3 rounded">
                              {currentSuggestion.additionalNotes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* 右侧：实时预览 */}
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-semibold mb-4">Live Preview</h3>
                    <div className="flex items-center justify-center">
                      {currentSuggestion && (
                        <div 
                          className={`${currentTheme.bgPattern} rounded-lg shadow-xl border-4 border-white transform transition-all duration-300 hover:scale-105`}
                          style={{
                            width: `${Math.min(currentLayout.width * 0.8, 400)}px`,
                            height: `${Math.min(currentLayout.height * 0.8, 480)}px`,
                            aspectRatio: `${currentLayout.width} / ${currentLayout.height}`
                          }}
                        >
                          <div className="h-full flex flex-col items-center justify-start p-4 relative overflow-hidden">
                            {/* Decorative border */}
                            <div className={`absolute inset-4 border-2 ${
                              selectedTheme === 'classic' ? 'border-red-400' :
                              selectedTheme === 'ocean' ? 'border-blue-400' :
                              selectedTheme === 'nature' ? 'border-green-400' : 'border-amber-400'
                            } rounded-lg pointer-events-none`}></div>

                            {currentLayout.orientation === 'vertical' ? (
                              <>
                                {/* 标题区域 */}
                                <div className="text-center mb-3">
                                  <h2 className={`${
                                    fontSize === 'small' ? 'text-2xl' : 
                                    fontSize === 'medium' ? 'text-3xl' : 'text-4xl'
                                  } font-bold ${currentTheme.textColor} mb-1`}>
                                    {currentSuggestion.chineseName}
                                  </h2>
                                  <p className={`${
                                    fontSize === 'small' ? 'text-sm' : 
                                    fontSize === 'medium' ? 'text-base' : 'text-lg'
                                  } text-gray-600`}>
                                    {currentSuggestion.pinyin}
                                  </p>
                                  {currentSuggestion.englishName && (
                                    <p className={`${
                                      fontSize === 'small' ? 'text-xs' : 
                                      fontSize === 'medium' ? 'text-sm' : 'text-base'
                                    } text-gray-500 italic`}>
                                      {currentSuggestion.englishName}
                                    </p>
                                  )}
                                </div>
                                
                                {/* 详细信息区域 */}
                                <div className="w-full space-y-2 text-left">
                                  <hr className="border-gray-300 mb-3" />
                                  
                                  {/* Surname Explanation */}
                                  <div>
                                    <h4 className={`${
                                      fontSize === 'small' ? 'text-xs' : 
                                      fontSize === 'medium' ? 'text-sm' : 'text-sm'
                                    } font-semibold ${currentTheme.accentColor} mb-1`}>
                                      👨‍👩‍👧‍👦 Surname Explanation
                                    </h4>
                                    <p className={`${
                                      fontSize === 'small' ? 'text-xs' : 
                                      fontSize === 'medium' ? 'text-xs' : 'text-xs'
                                    } text-gray-600 leading-tight`}>
                                      {currentSuggestion.surnameExplanation}
                                    </p>
                                  </div>

                                  {/* Given Name Meaning */}
                                  <div>
                                    <h4 className={`${
                                      fontSize === 'small' ? 'text-xs' : 
                                      fontSize === 'medium' ? 'text-sm' : 'text-sm'
                                    } font-semibold ${currentTheme.accentColor} mb-1`}>
                                      ✨ Given Name Meaning
                                    </h4>
                                    <p className={`${
                                      fontSize === 'small' ? 'text-xs' : 
                                      fontSize === 'medium' ? 'text-xs' : 'text-xs'
                                    } text-gray-600 leading-tight`}>
                                      {currentSuggestion.givenNameExplanation}
                                    </p>
                                  </div>

                                  {/* Cultural Connection */}
                                  <div>
                                    <h4 className={`${
                                      fontSize === 'small' ? 'text-xs' : 
                                      fontSize === 'medium' ? 'text-sm' : 'text-sm'
                                    } font-semibold ${currentTheme.accentColor} mb-1`}>
                                      🏮 Cultural Connection
                                    </h4>
                                    <p className={`${
                                      fontSize === 'small' ? 'text-xs' : 
                                      fontSize === 'medium' ? 'text-xs' : 'text-xs'
                                    } text-gray-600 leading-tight`}>
                                      {currentSuggestion.culturalConnection}
                                    </p>
                                  </div>

                                  {/* Additional Notes */}
                                  {currentSuggestion.additionalNotes && (
                                    <div>
                                      <h4 className={`${
                                        fontSize === 'small' ? 'text-xs' : 
                                        fontSize === 'medium' ? 'text-sm' : 'text-sm'
                                      } font-semibold ${currentTheme.accentColor} mb-1`}>
                                        💡 Additional Notes
                                      </h4>
                                      <p className={`${
                                        fontSize === 'small' ? 'text-xs' : 
                                        fontSize === 'medium' ? 'text-xs' : 'text-xs'
                                      } text-gray-600 leading-tight`}>
                                        {currentSuggestion.additionalNotes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </>
                                                        ) : currentLayout.orientation === 'horizontal' ? (
                               <div className="flex w-full h-full p-2">
                                 {/* 左侧：名字信息 */}
                                 <div className="flex-1 text-center">
                                   <h2 className={`${
                                     fontSize === 'small' ? 'text-lg' : 
                                     fontSize === 'medium' ? 'text-xl' : 'text-2xl'
                                   } font-bold ${currentTheme.textColor} mb-1`}>
                                     {currentSuggestion.chineseName}
                                   </h2>
                                   <p className={`${
                                     fontSize === 'small' ? 'text-xs' : 
                                     fontSize === 'medium' ? 'text-sm' : 'text-base'
                                   } text-gray-600`}>
                                     {currentSuggestion.pinyin}
                                   </p>
                                   {currentSuggestion.englishName && (
                                     <p className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-xs' : 'text-sm'
                                     } text-gray-500 italic`}>
                                       {currentSuggestion.englishName}
                                     </p>
                                   )}
                                 </div>
                                 
                                 <div className="border-l-2 border-gray-300 mx-2"></div>
                                 
                                 {/* 右侧：详细信息 */}
                                 <div className="flex-1 space-y-1 text-left overflow-hidden">
                                   <div>
                                     <h4 className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-xs' : 'text-sm'
                                     } font-semibold ${currentTheme.accentColor}`}>
                                       👨‍👩‍👧‍👦 Surname
                                     </h4>
                                     <p className="text-xs text-gray-600 leading-tight">
                                       {currentSuggestion.surnameExplanation.substring(0, 60)}...
                                     </p>
                                   </div>
                                   <div>
                                     <h4 className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-xs' : 'text-sm'
                                     } font-semibold ${currentTheme.accentColor}`}>
                                       ✨ Given Name
                                     </h4>
                                     <p className="text-xs text-gray-600 leading-tight">
                                       {currentSuggestion.givenNameExplanation.substring(0, 60)}...
                                     </p>
                                   </div>
                                   <div>
                                     <h4 className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-xs' : 'text-sm'
                                     } font-semibold ${currentTheme.accentColor}`}>
                                       🏮 Cultural
                                     </h4>
                                     <p className="text-xs text-gray-600 leading-tight">
                                       {currentSuggestion.culturalConnection.substring(0, 60)}...
                                     </p>
                                   </div>
                                 </div>
                               </div>
                                                          ) : (
                               <div className="text-center space-y-3">
                                 {/* 标题 */}
                                 <div>
                                   <h2 className={`${
                                     fontSize === 'small' ? 'text-2xl' : 
                                     fontSize === 'medium' ? 'text-3xl' : 'text-4xl'
                                   } font-bold ${currentTheme.textColor} mb-1`}>
                                     {currentSuggestion.chineseName}
                                   </h2>
                                   <p className={`${
                                     fontSize === 'small' ? 'text-sm' : 
                                     fontSize === 'medium' ? 'text-base' : 'text-lg'
                                   } text-gray-600`}>
                                     {currentSuggestion.pinyin}
                                   </p>
                                   {currentSuggestion.englishName && (
                                     <p className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-sm' : 'text-base'
                                     } text-gray-500 italic`}>
                                       {currentSuggestion.englishName}
                                     </p>
                                   )}
                                 </div>

                                 <hr className="border-gray-300 my-2" />

                                 {/* 详细信息 - 紧凑布局 */}
                                 <div className="space-y-2 text-left">
                                   <div>
                                     <h4 className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-sm' : 'text-sm'
                                     } font-semibold ${currentTheme.accentColor} mb-1`}>
                                       👨‍👩‍👧‍👦 Surname Explanation
                                     </h4>
                                     <p className="text-xs text-gray-600 leading-tight">
                                       {currentSuggestion.surnameExplanation.length > 80 ? 
                                         currentSuggestion.surnameExplanation.substring(0, 80) + '...' : 
                                         currentSuggestion.surnameExplanation}
                                     </p>
                                   </div>

                                   <div>
                                     <h4 className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-sm' : 'text-sm'
                                     } font-semibold ${currentTheme.accentColor} mb-1`}>
                                       ✨ Given Name Meaning
                                     </h4>
                                     <p className="text-xs text-gray-600 leading-tight">
                                       {currentSuggestion.givenNameExplanation.length > 80 ? 
                                         currentSuggestion.givenNameExplanation.substring(0, 80) + '...' : 
                                         currentSuggestion.givenNameExplanation}
                                     </p>
                                   </div>

                                   <div>
                                     <h4 className={`${
                                       fontSize === 'small' ? 'text-xs' : 
                                       fontSize === 'medium' ? 'text-sm' : 'text-sm'
                                     } font-semibold ${currentTheme.accentColor} mb-1`}>
                                       🏮 Cultural Connection
                                     </h4>
                                     <p className="text-xs text-gray-600 leading-tight">
                                       {currentSuggestion.culturalConnection.length > 80 ? 
                                         currentSuggestion.culturalConnection.substring(0, 80) + '...' : 
                                         currentSuggestion.culturalConnection}
                                     </p>
                                   </div>
                                 </div>
                               </div>
                             )}

                            {/* Decorative corners */}
                            <div className={`absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 ${
                              selectedTheme === 'classic' ? 'border-red-300' :
                              selectedTheme === 'ocean' ? 'border-blue-300' :
                              selectedTheme === 'nature' ? 'border-green-300' : 'border-amber-300'
                            }`}></div>
                            <div className={`absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 ${
                              selectedTheme === 'classic' ? 'border-red-300' :
                              selectedTheme === 'ocean' ? 'border-blue-300' :
                              selectedTheme === 'nature' ? 'border-green-300' : 'border-amber-300'
                            }`}></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about Chinese name generation
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Is the Chinese name generator free?
              </h3>
              <p className="text-gray-600">
                Yes, our Chinese name generator is completely free to use. You can generate unlimited Chinese names without any registration or payment required.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How accurate are the generated Chinese names?
              </h3>
              <p className="text-gray-600">
                Our AI-powered generator uses advanced algorithms to create culturally accurate Chinese names. Each name includes proper pinyin pronunciation, character meanings, and cultural significance based on traditional Chinese naming conventions.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Can I use the generated name for official purposes?
              </h3>
              <p className="text-gray-600">
                While our generator creates authentic Chinese names, we recommend consulting with native Chinese speakers or cultural experts before using a name for official documents or legal purposes.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How many name suggestions will I get?
              </h3>
              <p className="text-gray-600">
                You'll receive 3 unique Chinese name suggestions, each with different meanings, pronunciations, and cultural contexts. You can regenerate as many times as you like to explore more options.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                What is my Chinese name and how does the translation work?
              </h3>
              <p className="text-gray-600">
                We answer “what is my Chinese name” by linking your English name to characters that keep the pronunciation and convey positive themes. The process reminds you why “my name in Chinese” looks and sounds the way it does.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Can I get funny Chinese names or silly Chinese names?
              </h3>
              <p className="text-gray-600">
                Our generator keeps things meaningful, but we can lean playful if you like. Tell us you want a lighthearted tone and we will suggest names with fun twists while still honoring Chinese naming etiquette.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How do I see my name in Chinese with meanings?
              </h3>
              <p className="text-gray-600">
                Every result explains your name in Chinese and meanings, including the tone, symbol, and cultural story. Use those notes when sharing your Chinese name with friends or introducing yourself as “my name is in Chinese”.
              </p>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="mt-12 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Learn More About Chinese Names</h3>
            <p className="text-gray-600 mb-6">
              Explore our comprehensive guides to deepen your understanding of Chinese naming traditions and translation methods.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/blog/chinese-name-translation"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Chinese Name Translation Guide</h4>
                <p className="text-sm text-gray-600">
                  Learn how Chinese name translation works, including phonetic matching, meaning-based translation, and cultural considerations.
                </p>
              </Link>
              <Link
                to="/chinese-boy-names"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Chinese Boy Names</h4>
                <p className="text-sm text-gray-600">
                  Browse 200+ authentic Chinese boy names with meanings, from traditional classics to modern favorites.
                </p>
              </Link>
              <Link
                to="/chinese-girl-names"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Chinese Girl Names</h4>
                <p className="text-sm text-gray-600">
                  Discover beautiful Chinese girl names with meanings, from elegant traditional to modern favorites.
                </p>
              </Link>
              <Link
                to="/cute-chinese-girl-names"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Cute Chinese Girl Names</h4>
                <p className="text-sm text-gray-600">
                  Narrow your search to sweet, soft, and adorable female names with clear pinyin and meanings.
                </p>
              </Link>
              <Link
                to="/chinese-last-names"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Chinese Last Names</h4>
                <p className="text-sm text-gray-600">
                  Explore 100+ common Chinese surnames with origins, meanings, and cultural history.
                </p>
              </Link>
              <Link
                to="/funny-chinese-names"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Funny Chinese Names</h4>
                <p className="text-sm text-gray-600">
                  Explore playful naming ideas if you want a humorous online persona or character name.
                </p>
              </Link>
              <Link
                to="/chinese-pet-names"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Chinese Pet Names</h4>
                <p className="text-sm text-gray-600">
                  Browse Chinese dog and cat name ideas if you need cute creature naming inspiration.
                </p>
              </Link>
              <Link
                to="/chinese-dragon-names"
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Chinese Dragon Names</h4>
                <p className="text-sm text-gray-600">
                  Explore myth-inspired Chinese dragon names for fantasy characters, creatures, and worldbuilding.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
